import { operation } from "@/lib/api-client";
import { readAllRows } from "@/lib/read-all-rows";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { Palette, Plus, Loader2, Search, Trash2, Settings2, FileText } from "lucide-react";
import { AppLayout } from "@/components/app-layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { aws as awsClient, getCsrfToken } from "@/aws/client";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "sonner";
import { ContextualHelpLink } from "@/components/help/contextual-help-link";

export const Route = createFileRoute("/design/")({
  head: () => ({
    meta: [
      { title: "Design — AskBell" },
      {
        name: "description",
        content:
          "Agentic deck and document builder — produce on-brand PDFs and PowerPoints by chatting with AskBell.",
      },
    ],
  }),
  component: DesignIndexPage,
});

type DesignRow = {
  id: string;
  title: string;
  brief: string | null;
  design_system_id: string | null;
  status: string;
  updated_at: string;
};

type SystemRow = { id: string; name: string };

function DesignIndexPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState<DesignRow[] | null>(null);
  const [systems, setSystems] = useState<SystemRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const loadRequest = useRef(0);
  const [query, setQuery] = useState("");
  const [pendingDelete, setPendingDelete] = useState<DesignRow | null>(null);
  const [deleting, setDeleting] = useState(false);
  const deleteLock = useRef(false);

  const fetchAll = useCallback(async () => {
    const request=++loadRequest.current;
    const current=()=>request===loadRequest.current;
    if(!user){setRows([]);setLoading(false);return;}
    setLoading(true);setLoadError(null);
    try {
      const [designs, systems] = await Promise.all([
        readAllRows<DesignRow>((from,to)=>awsClient.from("designs").select("id, title, brief, design_system_id, status, updated_at").order("updated_at",{ascending:false}).order("id",{ascending:true}).range(from,to),current),
        readAllRows<SystemRow>((from,to)=>awsClient.from("design_systems").select("id, name").order("name").order("id",{ascending:true}).range(from,to),current),
      ]);
      if(!current())return;
      if(designs.error)throw new Error(designs.error.message);
      if(systems.error)throw new Error(systems.error.message);
      setRows(designs.data);setSystems(systems.data);
    } catch(error){if(current())setLoadError(error instanceof Error?error.message:"Could not load this library.");}
    finally {if(current())setLoading(false);}
  },[user]);

  useEffect(()=>{
    void fetchAll();
    return()=>{loadRequest.current++;};
  },[fetchAll]);

  const systemNameById = useMemo(() => {
    const m = new Map<string, string>();
    systems.forEach((s) => m.set(s.id, s.name));
    return m;
  }, [systems]);

  const visible = useMemo(() => {
    if (!rows) return [];
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.title.toLowerCase().includes(q) ||
        (r.brief ?? "").toLowerCase().includes(q),
    );
  }, [rows, query]);

  const requestDelete = (row: DesignRow, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setPendingDelete(row);
  };

  const confirmDelete = async () => {
    if (!pendingDelete || deleteLock.current) return;
    deleteLock.current = true;
    setDeleting(true);
    const target = pendingDelete;
    let error: unknown;
    try { await operation("designs_delete", {id:target.id}, getCsrfToken()); }
    catch (caught) { error = caught; }
    setDeleting(false);
    deleteLock.current = false;
    if (error) {
      toast.error("Couldn't delete design");
      return;
    }
    setRows((prev) => (prev ?? []).filter((r) => r.id !== target.id));
    setPendingDelete(null);
    toast.success("Design deleted");
  };

  return (
    <AppLayout>
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-5xl px-6 py-8">
          <div className="mb-6 flex flex-col items-start justify-between gap-4 sm:flex-row">
            <div>
              <div className="flex items-center gap-2">
                <Palette className="h-5 w-5 text-primary" />
                <h1 className="text-ui-2xl font-bold tracking-tight text-foreground">
                  Design
                </h1>
                <Badge
                  variant="outline"
                  className="border-primary/30 text-primary text-ui-xs uppercase tracking-wider"
                >
                  Beta
                </Badge>
              </div>
              <p className="text-ui-base text-muted-foreground mt-1">
                Chat with an agent to produce on-brand decks and documents. Pick
                a Design System and let the agent plan, draft, and refine.
              </p>
              <ContextualHelpLink topic="design" label="How does this work?" className="mt-2" />
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              <Button asChild variant="outline" className="gap-1.5" data-tour-design-systems-button="">
                <Link to="/design/systems">
                  <Settings2 className="h-4 w-4" />
                  Design Systems
                </Link>
              </Button>
              <Button asChild className="gap-1.5" data-tour-design-new-button="">
                <Link to="/design/new">
                  <Plus className="h-4 w-4" />
                  New design
                </Link>
              </Button>
            </div>
          </div>

          <div className="mb-4 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search designs…"
              className="pl-9"
            />
          </div>

          {loadError ? <div role="alert" className="rounded-ui-md border border-destructive/30 bg-destructive/5 p-5"><p className="text-ui-base">{loadError}</p><Button variant="outline" className="mt-3" onClick={()=>void fetchAll()}>Try again</Button></div> : loading ? (
            <div className="flex justify-center py-16 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : visible.length === 0 ? (
            <EmptyState hasAny={(rows ?? []).length > 0} />
          ) : (
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {visible.map((d) => (
                <li key={d.id} className="group/d">
                  <Link
                    to="/design/$designId"
                    params={{ designId: d.id }}
                    className="relative block rounded-ui-md border border-border bg-card hover:border-primary/40 hover:bg-secondary/30 transition-colors p-4"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="h-4 w-4 text-primary shrink-0" />
                        <span className="font-bold text-foreground truncate">
                          {d.title}
                        </span>
                      </div>
                      <button
                        onClick={(e) => requestDelete(d, e)}
                        aria-label="Delete design"
                        className="p-1 rounded hover:bg-secondary text-muted-foreground opacity-0 group-hover/d:opacity-100 hover:text-destructive transition-opacity"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    {d.brief && (
                      <p className="text-ui-sm text-muted-foreground mt-1.5 line-clamp-2 ml-6">
                        {d.brief}
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-3 ml-6 flex-wrap">
                      <Badge
                        variant="outline"
                        className="text-ui-xs uppercase tracking-wider"
                      >
                        {d.status}
                      </Badge>
                      {d.design_system_id && systemNameById.get(d.design_system_id) && (
                        <Badge variant="secondary" className="text-ui-xs">
                          {systemNameById.get(d.design_system_id)}
                        </Badge>
                      )}
                      <span className="text-ui-xs text-muted-foreground">
                        Updated {timeAgo(d.updated_at)}
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(open) => {
          if (!open && !deleteLock.current) setPendingDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this design?</AlertDialogTitle>
            <AlertDialogDescription>
              "{pendingDelete?.title}" and its history, uploads, and generated files will be removed. Separate editor sessions and their copies will be kept. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleting}
              onClick={(e) => {
                e.preventDefault();
                confirmDelete();
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}

function EmptyState({ hasAny }: { hasAny: boolean }) {
  return (
    <div className="text-center py-16 text-muted-foreground">
      <Palette className="h-10 w-10 mx-auto mb-3 opacity-40" />
      {hasAny ? (
        <p className="text-ui-base">No designs match your search.</p>
      ) : (
        <>
          <p className="text-ui-base font-bold text-foreground mb-1">
            No designs yet
          </p>
          <p className="text-ui-sm max-w-md mx-auto mb-4">
            Start a new design — describe what you want and AskBell will plan,
            draft, and refine an on-brand deck or document.
          </p>
          <Button asChild className="gap-1.5">
            <Link to="/design/new">
              <Plus className="h-4 w-4" />
              New design
            </Link>
          </Button>
        </>
      )}
    </div>
  );
}

function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return `${Math.floor(d / 30)}mo ago`;
}
